import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { UserContext } from './App';
import { ChatContext } from './ChatContext';
import ChatSidebar from './ChatSidebar';
import { ChatMessageList, ChatInputBar } from './ChatPanel';
import { motion, AnimatePresence } from 'framer-motion';
import { userAPI, authAPI } from './api';
import {
  FaUser, FaHome, FaCoins, FaStar, FaBoxes, FaBoxOpen,
  FaCog, FaGift, FaSignInAlt, FaTimes, FaComments,
  FaFireAlt, FaHeadset, FaChartLine, FaChartBar, FaBell, FaWallet,
  FaTrophy, FaLevelUpAlt, FaGamepad, FaChevronDown,
  FaUserCircle, FaTh, FaMedal, FaMoneyBillWave, FaTags,
  FaAngleDown, FaSignOutAlt, FaCaretDown, FaHandHoldingUsd,
  FaListOl, FaPaperPlane, FaFingerprint, FaImage, FaInfoCircle,
  FaUsers, FaShare, FaDiscord, FaTwitter, FaInstagram, FaTelegram, FaTwitch, FaCheck, FaSpinner, FaCopy,
  FaLock, FaShieldAlt, FaBullseye, FaExchangeAlt, FaExclamationCircle, 
  FaExclamationTriangle, FaMinusCircle, FaBook, FaCheckCircle, FaArrowDown, FaArrowUp,
  FaLink, FaEnvelope, FaHistory, FaCrosshairs, FaBomb, FaSyncAlt, FaBolt, FaSync, FaPlus
} from 'react-icons/fa';

const ScrapIcon = ({ size = 14 }) => (
    <img 
        src="/scrap.webp" 
        alt="scrap" 
        style={{ width: size, height: size, objectFit: 'contain' }} 
    />
);
import Footer from './Footer';
import './Dashboard.css';

// Case Opening Animation Component
const CaseOpeningAnimation = ({ isOpen, onClose, caseType, onOpenCase }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [finalReward, setFinalReward] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [rollItems, setRollItems] = useState([]);
  const [rollDistance, setRollDistance] = useState(0);

  // Sound effects
  const openingSoundRef = useRef(new Audio('/sounds/case-opening.mp3'));
  const rollingSoundRef = useRef(new Audio('/sounds/rolling.mp3'));
  const winSoundRef = useRef(new Audio('/sounds/win.mp3'));

  useEffect(() => {
    // Set volumes
    if (openingSoundRef.current) openingSoundRef.current.volume = 0.6;
    if (rollingSoundRef.current) {
      rollingSoundRef.current.volume = 0.4;
      rollingSoundRef.current.loop = true;
    }
    if (winSoundRef.current) winSoundRef.current.volume = 0.7;

    // Clean up sounds
    return () => {
      if (openingSoundRef.current) {
        openingSoundRef.current.pause();
        openingSoundRef.current.currentTime = 0;
      }
      if (rollingSoundRef.current) {
        rollingSoundRef.current.pause();
        rollingSoundRef.current.currentTime = 0;
      }
      if (winSoundRef.current) {
        winSoundRef.current.pause();
        winSoundRef.current.currentTime = 0;
      }
    };
  }, []);

  // Define possible rewards based on case rarity
  const caseRewards = {
    common: [
      { name: 'Bronze Coin', value: 0.10, rarity: 'Common', emoji: '🥉', color: '#cd7f32' },
      { name: 'Silver Coin', value: 0.25, rarity: 'Uncommon', emoji: '🥈', color: '#c0c0c0' },
      { name: 'Gold Coin', value: 0.50, rarity: 'Rare', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 1.00, rarity: 'Epic', emoji: '💎', color: '#4a6cff' },
      { name: 'Ruby', value: 2.00, rarity: 'Legendary', emoji: '💍', color: '#ff6b35' }
    ],
    rare: [
      { name: 'Silver Coin', value: 0.50, rarity: 'Common', emoji: '🥈', color: '#c0c0c0' },
      { name: 'Gold Coin', value: 1.00, rarity: 'Uncommon', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 2.50, rarity: 'Rare', emoji: '💎', color: '#4a6cff' },
      { name: 'Ruby', value: 5.00, rarity: 'Epic', emoji: '💍', color: '#ff6b35' },
      { name: 'Crown', value: 10.00, rarity: 'Legendary', emoji: '👑', color: '#ffb703' }
    ],
    epic: [
      { name: 'Gold Coin', value: 2.00, rarity: 'Common', emoji: '🥇', color: '#ffd700' },
      { name: 'Diamond', value: 5.00, rarity: 'Uncommon', emoji: '💎', color: '#4a6cff' },
      { name: 'Ruby', value: 10.00, rarity: 'Rare', emoji: '💍', color: '#ff6b35' },
      { name: 'Crown', value: 25.00, rarity: 'Epic', emoji: '👑', color: '#ffb703' },
      { name: 'Star', value: 50.00, rarity: 'Legendary', emoji: '⭐', color: '#ff3b30' }
    ]
  };

  const rewards = caseRewards[caseType] || caseRewards.common;

  // Generate fake items for rolling animation with precise positioning
  const generateRollItems = (targetReward) => {
    const items = [];
    const totalItems = 50; // Total items in the roll
    const targetIndex = 25; // Target will be at index 25 (middle-ish)

    // Add random items before the target
    for (let i = 0; i < targetIndex; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({ ...randomReward, id: `fake-${i}`, index: i });
    }

    // Add the target reward at the calculated position
    items.push({ ...targetReward, id: 'target', index: targetIndex });

    // Add random items after the target
    for (let i = targetIndex + 1; i < totalItems; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({ ...randomReward, id: `fake-${i}`, index: i });
    }

    return items;
  };

  // Calculate the exact distance to roll so the target item lands at the pointer
  const calculateRollDistance = (targetIndex) => {
    const itemWidth = 120; // Width of each roll item including gap
    const containerWidth = 500; // Width of the roll container
    const centerOffset = containerWidth / 2; // Center of the container
    const targetPosition = targetIndex * itemWidth; // Position of target item

    // Calculate distance to move so target item ends up in center
    // Add extra offset to ensure proper alignment
    return -(targetPosition - centerOffset + itemWidth / 2) + 60;
  };

  const handleOpenCase = async () => {
    setIsOpening(true);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);

    try {
      // Play opening sound
      if (openingSoundRef.current) {
        openingSoundRef.current.currentTime = 0;
        openingSoundRef.current.play().catch(err => console.log('Could not play opening sound:', err));
      }

      // Call the actual API to open the case
      const result = await onOpenCase(caseType);

      if (result.success) {
        // Use the exact reward from the backend response
        const backendReward = {
          name: result.item.name,
          value: result.item.value,
          rarity: result.item.rarity,
          emoji: result.item.emoji || '🎁',
          color: result.item.color || '#4a6cff'
        };

        setFinalReward(backendReward);

        // Generate roll items with target positioned correctly
        const items = generateRollItems(backendReward);
        setRollItems(items);

        // Calculate exact roll distance to land on target
        const targetIndex = items.findIndex(item => item.id === 'target');
        const distance = calculateRollDistance(targetIndex);
        setRollDistance(distance);

        // Show case opening animation for 2 seconds
        setTimeout(() => {
          setIsOpening(false);
          setIsRolling(true);

          // Play rolling sound
          if (rollingSoundRef.current) {
            rollingSoundRef.current.currentTime = 0;
            rollingSoundRef.current.play().catch(err => console.log('Could not play rolling sound:', err));
          }

          // Start rolling animation for 3 seconds
          setTimeout(() => {
            // Stop rolling sound
            if (rollingSoundRef.current) {
              rollingSoundRef.current.pause();
              rollingSoundRef.current.currentTime = 0;
            }

            // Play win sound
            if (winSoundRef.current) {
              winSoundRef.current.currentTime = 0;
              winSoundRef.current.play().catch(err => console.log('Could not play win sound:', err));
            }

            setIsRolling(false);
            setShowResult(true);
          }, 3000);
        }, 2000);
      }
    } catch (error) {
      console.error('Error opening case:', error);
      setIsOpening(false);
      if(window._addToast) window._addToast({type:'error', title:'CASE ERROR', message:'Failed to open case'});
    }
  };

  const handleClose = () => {
    // Stop all sounds
    if (openingSoundRef.current) {
      openingSoundRef.current.pause();
      openingSoundRef.current.currentTime = 0;
    }
    if (rollingSoundRef.current) {
      rollingSoundRef.current.pause();
      rollingSoundRef.current.currentTime = 0;
    }
    if (winSoundRef.current) {
      winSoundRef.current.pause();
      winSoundRef.current.currentTime = 0;
    }

    setIsOpening(false);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="case-opening-overlay">
      <motion.div
        className="case-opening-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="modal-header">
          <h2>📦 Case Opening</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {!isOpening && !isRolling && !showResult && (
            <div className="case-preview-section">
              <div className="case-display">
                <img src="case.gif" alt="Case" className="case-image" />
                <h3>Open {caseType} Case</h3>
                <p>Click to open and see what's inside!</p>
                <button className="open-case-btn" onClick={handleOpenCase}>
                  🎲 Open Case
                </button>
              </div>
            </div>
          )}

          {isOpening && (
            <div className="case-opening-section">
              <h3>📦 Opening your case...</h3>
              <div className="case-opening-animation">
                <img src="case.gif" alt="Opening Case" className="opening-case-gif" />
                <div className="opening-sparkles">
                  ✨ ⭐ 💫 ✨ ⭐
                </div>
              </div>
            </div>
          )}

          {isRolling && finalReward && rollItems.length > 0 && (
            <div className="rolling-section">
              <h3>🎰 Rolling for your reward...</h3>
              <div className="roll-container">
                <div className="roll-window">
                  <motion.div
                    className="roll-items"
                    animate={{ x: rollDistance }}
                    transition={{
                      duration: 3,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                  >
                    {rollItems.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className="roll-item"
                        style={{ backgroundColor: item.color }}
                      >
                        <div className="item-emoji">{item.emoji}</div>
                        <div className="item-name">{item.name}</div>
                        <div className="item-value">${item.value.toFixed(2)}</div>
                        <div className="item-rarity">{item.rarity}</div>
                      </div>
                    ))}
                  </motion.div>
                  <div className="roll-pointer"></div>
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {showResult && finalReward && (
              <motion.div
                className="result-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
              >
                <div className="result-card" style={{ borderColor: finalReward.color }}>
                  <div className="result-icon" style={{ backgroundColor: finalReward.color }}>
                    {finalReward.emoji}
                  </div>
                  <h3>🎉 Congratulations!</h3>
                  <div className="result-details">
                    <div className="rarity-badge" style={{ backgroundColor: finalReward.color }}>
                      {finalReward.rarity}
                    </div>
                    <div className="reward-name">{finalReward.name}</div>
                    <div className="reward-amount">
                      ${finalReward.value.toFixed(2)}
                    </div>
                  </div>
                  <button className="collect-btn" onClick={handleClose}>
                    Collect Reward
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

// Fixed Free Coins Animation Component
const FreeCoinsAnimation = ({ isOpen, onClose, onClaim }) => {
  // Add class to body when modal is open to hide footer
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const [isOpening, setIsOpening] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const [finalReward, setFinalReward] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [rollItems, setRollItems] = useState([]);
  const [rollDistance, setRollDistance] = useState(0);

  // Sound effects
  const openingSoundRef = useRef(new Audio('/sounds/case-opening.mp3'));
  const rollingSoundRef = useRef(new Audio('/sounds/rolling.mp3'));
  const winSoundRef = useRef(new Audio('/sounds/win.mp3'));

  useEffect(() => {
    // Set volumes
    if (openingSoundRef.current) openingSoundRef.current.volume = 0.6;
    if (rollingSoundRef.current) {
      rollingSoundRef.current.volume = 0.4;
      rollingSoundRef.current.loop = true;
    }
    if (winSoundRef.current) winSoundRef.current.volume = 0.7;

    return () => {
      // Clean up sounds
      if (openingSoundRef.current) {
        openingSoundRef.current.pause();
        openingSoundRef.current.currentTime = 0;
      }
      if (rollingSoundRef.current) {
        rollingSoundRef.current.pause();
        rollingSoundRef.current.currentTime = 0;
      }
      if (winSoundRef.current) {
        winSoundRef.current.pause();
        winSoundRef.current.currentTime = 0;
      }
    };
  }, []);

  // Define all possible rewards with their data
  const rewards = [
    { value: 1.00, chance: 1, color: '#ff6b35', rarity: 'Legendary', emoji: '💎', name: 'Legendary Prize' },
    { value: 0.50, chance: 9, color: '#ffb703', rarity: 'Epic', emoji: '⭐', name: 'Epic Prize' },
    { value: 0.20, chance: 50, color: '#3b82f6', rarity: 'Rare', emoji: '💫', name: 'Rare Prize' },
    { value: 0.10, chance: 40, color: '#10b981', rarity: 'Common', emoji: '🪙', name: 'Common Prize' }
  ];

  // Generate fake items for rolling animation with precise positioning
  const generateRollItems = (targetReward) => {
    const items = [];
    const totalItems = 60; // Increased for smoother animation
    const targetIndex = 30; // Target will be at index 30 (middle)

    // Add random items before the target
    for (let i = 0; i < targetIndex; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({
        ...randomReward,
        id: `fake-${i}`,
        index: i,
        name: randomReward.name
      });
    }

    // Add the target reward at the calculated position
    items.push({
      ...targetReward,
      id: 'target',
      index: targetIndex,
      name: targetReward.name || 'Prize'
    });

    // Add random items after the target
    for (let i = targetIndex + 1; i < totalItems; i++) {
      const randomReward = rewards[Math.floor(Math.random() * rewards.length)];
      items.push({
        ...randomReward,
        id: `fake-${i}`,
        index: i,
        name: randomReward.name
      });
    }

    return items;
  };

  const calculateRollDistance = (targetIndex) => {
    const itemWidth = 120; // Width of each roll item including gap
    const containerWidth = 500; // Width of the roll container
    const centerOffset = containerWidth / 2; // Center of the container
    const targetPosition = targetIndex * itemWidth; // Position of target item

    // Calculate distance to move so target item ends up in center
    return -(targetPosition - centerOffset + itemWidth / 2) + 60;
  };

  const handleClaim = async () => {
    setIsOpening(true);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);

    try {
      // Play opening sound
      if (openingSoundRef.current) {
        openingSoundRef.current.currentTime = 0;
        openingSoundRef.current.play().catch(err => console.log('Could not play opening sound:', err));
      }

      // Call the actual API to get the reward
      const result = await onClaim();

      if (result.success) {
        // Find the matching reward from our predefined list - FIXED LOGIC
        const matchedReward = rewards.find(r =>
          Math.abs(r.value - result.amount) < 0.01
        ) || rewards[2]; // Default to $0.20 if no match found

        const finalRewardData = {
          ...matchedReward,
          actualAmount: result.amount,
          casesAwarded: result.casesAwarded || 0
        };

        setFinalReward(finalRewardData);

        // Generate roll items with target positioned correctly
        const items = generateRollItems(finalRewardData);
        setRollItems(items);

        // Calculate exact roll distance to land on target
        const targetIndex = items.findIndex(item => item.id === 'target');
        const distance = calculateRollDistance(targetIndex);
        setRollDistance(distance);

        // Show case opening animation for 1.5 seconds
        setTimeout(() => {
          setIsOpening(false);
          setIsRolling(true);

          // Start rolling sound
          if (rollingSoundRef.current) {
            rollingSoundRef.current.currentTime = 0;
            rollingSoundRef.current.play().catch(err => console.log('Could not play rolling sound:', err));
          }

          // Start rolling animation for 3 seconds (smoother timing)
          setTimeout(() => {
            // Stop rolling sound
            if (rollingSoundRef.current) {
              rollingSoundRef.current.pause();
              rollingSoundRef.current.currentTime = 0;
            }

            setIsRolling(false);
            setShowResult(true);

            // Play win sound based on rarity
            if (winSoundRef.current) {
              winSoundRef.current.currentTime = 0;
              winSoundRef.current.play().catch(err => console.log('Could not play win sound:', err));
            }
          }, 3000);
        }, 1500);
      }
    } catch (error) {
      console.error('Error claiming free coins:', error);
      setIsOpening(false);
      if(window._addToast) window._addToast({type:'error', title:'REWARDS ERROR', message:'Failed to claim free coins'});
    }
  };

  const handleClose = () => {
    // Stop all sounds
    if (openingSoundRef.current) {
      openingSoundRef.current.pause();
      openingSoundRef.current.currentTime = 0;
    }
    if (rollingSoundRef.current) {
      rollingSoundRef.current.pause();
      rollingSoundRef.current.currentTime = 0;
    }
    if (winSoundRef.current) {
      winSoundRef.current.pause();
      winSoundRef.current.currentTime = 0;
    }

    setIsOpening(false);
    setIsRolling(false);
    setShowResult(false);
    setFinalReward(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="free-coins-overlay">
      <motion.div
        className="free-coins-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="modal-header">
          <h2>🎁 Free Coins Reward</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="modal-content">
          {!isOpening && !isRolling && !showResult && (
            <div className="claim-section">
              <div className="reward-preview">
                <div className="case-display">
                  <img src="case.gif" alt="Free Case" className="free-case-image" />
                  <h3>Claim Your Free Case!</h3>
                  <p>Open this case to discover what's inside:</p>
                </div>
                <div className="reward-chances">
                  {rewards.map((reward, index) => (
                    <div key={index} className="chance-item" style={{ borderLeft: `4px solid ${reward.color}` }}>
                      <span className="rarity">{reward.rarity}</span>
                      <span className="amount">${reward.value.toFixed(2)}</span>
                      <span className="chance">{reward.chance}%</span>
                    </div>
                  ))}
                </div>
                <button className="claim-btn" onClick={handleClaim}>
                  🎰 Roll Now
                </button>
              </div>
            </div>
          )}

          {isOpening && (
            <div className="case-opening-section">
              <h3>📦 Opening your free case...</h3>
              <div className="case-opening-animation">
                <img src="case.gif" alt="Opening Case" className="opening-case-gif" />
                <div className="opening-sparkles">
                  ✨ ⭐ 💫 ✨ ⭐
                </div>
              </div>
            </div>
          )}

          {isRolling && finalReward && rollItems.length > 0 && (
            <div className="rolling-section">
              <h3>🎰 Rolling for your reward...</h3>
              <div className="roll-container">
                <div className="roll-window">
                  <motion.div
                    className="roll-items"
                    animate={{ x: rollDistance }}
                    transition={{
                      duration: 3,
                      ease: [0.25, 0.46, 0.45, 0.94] // Smoother easing
                    }}
                  >
                    {rollItems.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className="roll-item"
                        style={{ backgroundColor: item.color }}
                      >
                        <div className="item-emoji">{item.emoji}</div>
                        <div className="item-name">{item.name}</div>
                        <div className="item-value">${item.value.toFixed(2)}</div>
                        <div className="item-rarity">{item.rarity}</div>
                      </div>
                    ))}
                  </motion.div>
                  <div className="roll-pointer"></div>
                </div>
              </div>
            </div>
          )}

          <AnimatePresence>
            {showResult && finalReward && (
              <motion.div
                className="result-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
              >
                <div className="result-card" style={{ borderColor: finalReward.color }}>
                  <div className="result-icon" style={{ backgroundColor: finalReward.color }}>
                    {finalReward.emoji}
                  </div>
                  <h3>🎉 Congratulations!</h3>
                  <div className="result-details">
                    <div className="rarity-badge" style={{ backgroundColor: finalReward.color }}>
                      {finalReward.rarity}
                    </div>
                    <div className="reward-amount">
                      ${finalReward.actualAmount.toFixed(2)}
                    </div>
                    <div className="reward-chance">
                      {finalReward.chance}% chance
                    </div>
                    {finalReward.casesAwarded > 0 && (
                      <div className="cases-info">
                        + {finalReward.casesAwarded} free case{finalReward.casesAwarded !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                  <button className="collect-btn" onClick={() => {
                    // Play a small success sound on collect
                    if (winSoundRef.current) {
                      const collectSound = winSoundRef.current.cloneNode();
                      collectSound.volume = 0.3;
                      collectSound.play().catch(err => console.log('Could not play collect sound:', err));
                    }
                    handleClose();
                  }}>
                    Collect Reward
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

const BACKEND_URL = window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://test3-0ar4.onrender.com';

const LOCKED_TABS = ['rewards', 'free-coins'];

function Dashboard() {
  const { 
    user, setUser, setActiveBalance, activeBalance, 
    setIsRedeemModalOpen,
    setIsDepositModalOpen, setIsWithdrawModalOpen,
    isAuthModalOpen, setIsAuthModalOpen,
    setAuthModalView 
  } = useContext(UserContext);
  const { chatMessages, sendMessage } = useContext(ChatContext);
  const navigate = useNavigate();
  const { tab } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const locationTab = searchParams.get('tab');

  // State 
  const [activeTab, setActiveTab] = useState(() => {
    const t = locationTab || tab || 'home';
    if (t === 'betting-history' || t === 'transactions' || t === 'your-cases') return 'profile';
    return t;
  });

  const [profileTab, setProfileTab] = useState(() => {
    const t = locationTab || tab || 'home';
    if (t === 'betting-history') return 'history';
    if (t === 'transactions') return 'transactions';
    if (t === 'your-cases') return 'inventory';
    return 'profile';
  });

  const [gameWagers, setGameWagers] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (locationTab) {
      if (locationTab === 'betting-history') {
        setActiveTab('profile');
        setProfileTab('history');
      } else if (locationTab === 'transactions') {
        setActiveTab('profile');
        setProfileTab('transactions');
      } else if (locationTab === 'your-cases') {
        setActiveTab('profile');
        setProfileTab('inventory');
      } else if (locationTab !== activeTab) {
        setActiveTab(locationTab);
      }
      window.scrollTo(0, 0);
    }
  }, [locationTab]);

  const [loading, setLoading] = useState(false);

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mutedUsers, setMutedUsers] = useState([]);
  const [showChat, setShowChat] = useState(() => {
    const saved = localStorage.getItem('showChat');
    if (saved !== null) return JSON.parse(saved);
    // Default to closed on mobile
    return window.innerWidth > 768;
  });

  // User and profile state
  const [tipUsername, setTipUsername] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [tipStatus, setTipStatus] = useState(null);
  const [historyLimit, setHistoryLimit] = useState(5);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyingWager, setVerifyingWager] = useState(null);
  const [tradeLinkInput, setTradeLinkInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState(null);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null);
  
  // Referral application state
  const [referralInputCode, setReferralInputCode] = useState('');
  const [isApplyingReferral, setIsApplyingReferral] = useState(false);
  const [applyReferralStatus, setApplyReferralStatus] = useState(null);
  const [referralLimit, setReferralLimit] = useState(5);
  const [newReferralCode, setNewReferralCode] = useState('');
  const [isChangingCode, setIsChangingCode] = useState(false);

  // Settings state
  const [clickVolume, setClickVolume] = useState(
    parseFloat(localStorage.getItem('clickVolume')) || 0.7
  );
  const [brightness, setBrightness] = useState(
    parseFloat(localStorage.getItem('brightness')) || 1.0
  );

  // Game and UI state
  const [hoveredGame, setHoveredGame] = useState(null);
  const [showGamesMenu, setShowGamesMenu] = useState(false);
  const [showBalancePopup, setShowBalancePopup] = useState(false);
  const [onlineUsers] = useState([
    { id: 1, username: 'Player1' },
    { id: 2, username: 'Player2' },
    { id: 3, username: 'Player3' },
  ]);
  const [socket, setSocket] = useState(null);

  // Free coins animation state
  const [showFreeCoinsModal, setShowFreeCoinsModal] = useState(false);

  // Case opening animation state
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState(null);

  // New states for updated features
  const [referralData, setReferralData] = useState(null);
  const [freeCoinsClaimed, setFreeCoinsClaimed] = useState(false);
  const [userCases, setUserCases] = useState({});
  const [availableCases, setAvailableCases] = useState([]);
  const [caseHistory, setCaseHistory] = useState([]);
  const [openingCase, setOpeningCase] = useState(false);

  // Wagering state
  const [wageringStatus, setWageringStatus] = useState(null);
  const [wageringLoading, setWageringLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (message, type = 'error') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const chatEndRef = useRef(null);
  const clickSound = useRef(new Audio('/sounds/click.mp3'));
  const balanceRef = useRef(null);

  // Game mode GIFs (replace with your actual GIF paths)
  const gameGifs = {
    coinflip: '/gifs/coinflip-preview.gif',
    roulette: '/gifs/roulette-preview.gif',
    upgrader: '/gifs/upgrader-preview.gif',

  };

  // Check if user can withdraw based on wagering requirements
  const canWithdraw = wageringStatus?.canWithdraw !== false;

  // Handle URL parameters for tab navigation
  const handleUpdateAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected for avatar:', file.name, file.size, file.type);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.error('Invalid file type:', file.type);
      setAvatarMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      setAvatarMessage({ type: 'error', text: 'File is too large (max 5MB)' });
      return;
    }

    setIsUpdatingAvatar(true);
    setAvatarMessage(null);

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      console.log('Sending upload request...');
      const response = await userAPI.uploadAvatar(formData);
      const data = response.data;
      console.log('Upload response:', response.status, data);
      
      if (response.status === 200 || response.status === 201) {
        setAvatarMessage({ type: 'success', text: 'Avatar successfully updated!' });
        setUser({ ...user, avatar: data.avatar });
        
        // Update localStorage to persist across refreshes
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...storedUser, avatar: data.avatar }));
      } else {
        setAvatarMessage({ type: 'error', text: data.error || 'Failed to update avatar' });
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Server error updating avatar. Ensure your file is an image under 5MB.';
      setAvatarMessage({ type: 'error', text: errorMsg });
    } finally {
      setIsUpdatingAvatar(false);
      // Reset the target value so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleUpdateEmail = async () => {
    if (!emailInput || !emailInput.includes('@')) {
      setEmailStatus({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    setIsUpdatingEmail(true);
    setEmailStatus(null);

    try {
      const token = localStorage.getItem('token');
      const response = await userAPI.updateEmail({ email: emailInput });

      if (response.status === 200) {
        const newEmail = response.data.email || emailInput;
        setEmailStatus({ type: 'success', text: response.data.message || 'Email updated! Check your inbox for verification.' });
        setUser({ ...user, email: newEmail, emailVerified: false });
        
        // Update localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...storedUser, email: newEmail, emailVerified: false }));
        setEmailInput('');
      }
    } catch (err) {
      console.error('Email update error:', err);
      const errorMsg = err.response?.data?.error || 'Failed to update email';
      setEmailStatus({ type: 'error', text: errorMsg });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleUpdateUsername = async () => {
    if (!usernameInput || usernameInput === user?.username) return;
    
    setIsUpdatingUsername(true);
    setUsernameStatus(null);
    
    try {
      const response = await userAPI.profile({ username: usernameInput });
      
      if (response.status === 200) {
        const newUsername = response.data.username || usernameInput;
        
        // Update local user state
        setUser(prev => ({
          ...prev,
          username: newUsername
        }));
        
        // If a new token was returned, update it in localStorage
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        
        // Update user in localStorage
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...storedUser, username: newUsername }));
        
        setUsernameStatus({ type: 'success', text: 'Username updated successfully!' });
        setUsernameInput('');
        
        // Add a success toast if window._addToast exists
        if (window._addToast) {
          window._addToast({
            type: 'success',
            title: 'Profile Updated',
            message: 'Your username has been changed.'
          });
        }
      }
    } catch (err) {
      console.error('Username update error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || 'Failed to update username';
      setUsernameStatus({ type: 'error', text: errorMsg });
    } finally {
      setIsUpdatingUsername(false);
    }
  };

  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');

      const validTabs = [
        'home', 'earn-with-us', 'leaderboard', 'rewards',
        'free-coins', 'profile', 'wallet', 'settings',
        'tos', 'privacy'
      ];

      if (tabParam && validTabs.includes(tabParam)) {
        setActiveTab(tabParam);
      }
    };

    // Handle initial load
    handleUrlChange();

    // Handle browser back/forward buttons
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);
  // Fetch Game History and Transactions when profile tabs are active
  useEffect(() => {
    if (activeTab === 'profile') {
      const fetchData = async () => {
        try {
          const res = await userAPI.transactions();
          // Assuming the API returns mixed transactions or has a structure we can use
          if (res.data.transactions) {
            // Filter game history for 'history' tab
            const history = res.data.transactions.filter(t => t.type === 'game' || t.gameType);
            setGameWagers(history);
            
            // Filter financial transactions for 'transactions' tab
            const trans = res.data.transactions.filter(t => t.type === 'deposit' || t.type === 'withdrawal' || t.type === 'tip');
            setTransactions(trans);
          }
        } catch (err) {
          console.error('Error fetching profile data:', err);
        }
      };
      fetchData();
    }
  }, [activeTab, profileTab]);

  // Sync inputs with user data and fetch wagering stats
  useEffect(() => {
    if (user) {
      if (!usernameInput) setUsernameInput(user.username || '');
      if (!emailInput) setEmailInput(user.email || '');
      
      const fetchStats = async () => {
        try {
          // Fetch wagering requirement
          const transRes = await userAPI.transactions();
          if (transRes.data.wagering) {
            setWageringStatus(transRes.data.wagering);
          }
          
          // Fetch game-specific stats (Jackpot, CF, Cases)
          const levelRes = await userAPI.level();
          if (levelRes.data) {
            setUserLevel(levelRes.data);
          }
        } catch (err) {
          console.error('Error fetching user stats:', err);
        }
      };
      fetchStats();
    }
  }, [user]);

  const renderVerifyModal = () => {
    if (!verifyingWager) return null;
    const BRAND_PURPLE = '#ffb703';

    return (
      <AnimatePresence>
        {showVerifyModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setShowVerifyModal(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }}
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              style={{ position: 'relative', width: '100%', maxWidth: '500px', background: '#16161c', borderRadius: '32px', padding: '40px', border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 50px 100px -20px rgba(0,0,0,0.5)' }}
            >
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                 <div style={{ width: '60px', height: '60px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4ade80', margin: '0 auto 15px' }}>
                   <FaShieldAlt size={24} />
                 </div>
                 <h2 style={{ fontSize: '24px', fontWeight: '900', color: 'white', margin: '0 0 5px 0' }}>VERIFY GAME</h2>
                 <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontWeight: '700' }}>ID: {verifyingWager._id}</p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {[
                  { label: 'SERVER SEED', value: verifyingWager.serverSeed || 'N/A' },
                  { label: 'CLIENT SEED', value: verifyingWager.clientSeed || 'rustycoin_v1' },
                  { label: 'NONCE', value: verifyingWager.nonce || 0 },
                  { label: 'RESULT HASH', value: verifyingWager.resultHash || 'Generating...' }
                ].map((row, i) => (
                  <div key={i} style={{ padding: '15px', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ fontSize: '10px', color: BRAND_PURPLE, fontWeight: '900', marginBottom: '4px' }}>{row.label}</div>
                    <div style={{ fontSize: '12px', color: 'white', fontWeight: '700', wordBreak: 'break-all', fontFamily: 'monospace' }}>{row.value}</div>
                  </div>
                ))}
              </div>

              <button 
                onClick={() => setShowVerifyModal(false)}
                style={{ width: '100%', marginTop: '30px', padding: '16px', background: BRAND_PURPLE, color: 'white', border: 'none', borderRadius: '16px', fontWeight: '900', fontSize: '14px', cursor: 'pointer', transition: '0.2s' }}
              >
                 CLOSE VERIFICATION
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    );
  };

  // Initialize socket connection for wallet updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && user) {
      let newSocket;

      try {
        newSocket = io(BACKEND_URL, {
          auth: { token },
          transports: ['websocket', 'polling'],
          timeout: 20000,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5,
          maxReconnectionAttempts: 10
        });

        newSocket.on('connect', () => {
          console.log('Socket connected in Dashboard');
          // Authenticate with socket to join user's room
          newSocket.emit('authenticate', { userId: user.id });
        });

        newSocket.on('connect_error', (error) => {
          console.error('Socket connection error:', error);
        });

        newSocket.on('disconnect', (reason) => {
          console.log('Socket disconnected in Dashboard:', reason);
          if (reason === 'io server disconnect') {
            newSocket.connect();
          }
        });

        newSocket.on('reconnect', (attemptNumber) => {
          console.log('Socket reconnected after', attemptNumber, 'attempts');
        });

        newSocket.on('reconnect_error', (error) => {
          console.error('Socket reconnection error:', error);
        });

        newSocket.on('reconnect_failed', () => {
          console.error('Socket failed to reconnect after maximum attempts');
        });

        // Balance updates are now handled globally in App.js via the ChatProvider socket
        // to ensure consistency across all game pages and navigation states.

        newSocket.on('chatError', (msg) => {
          addNotification(msg, 'error');
        });

        newSocket.on('notification', (data) => {
          addNotification(data.message || data, data.type || 'info');
        });

        setSocket(newSocket);

      } catch (error) {
        console.error('Error creating socket connection:', error);
      }

      return () => {
        if (newSocket) {
          newSocket.off();
          newSocket.disconnect();
        }
      };
    }
  }, [user, setUser]);

  useEffect(() => {
    clickSound.current.volume = clickVolume;
    clickSound.current.load();
  }, [clickVolume]);

  useEffect(() => {
    if (showChat) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, showChat]);

  useEffect(() => {
    document.documentElement.style.filter = `brightness(${brightness})`;
  }, [brightness]);

  // Load Tawk.to chat widget
  useEffect(() => {
    const existingScript = document.getElementById('tawkto-script');
    if (existingScript) {
      existingScript.remove();
    }

    var s1 = document.createElement("script");
    s1.async = true;
    s1.src = 'https://embed.tawk.to/684724ab14a7fb190e76cb11/1itauefs6';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s1.id = 'tawkto-script';
    document.body.appendChild(s1);

    return () => {
      const script = document.getElementById('tawkto-script');
      if (script) {
        script.remove();
      }
    };
  }, []);

  // Close balance popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (balanceRef.current && !balanceRef.current.contains(event.target)) {
        setShowBalancePopup(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check free coins status on load
  useEffect(() => {
    const checkFreeCoinStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/user/free-coins-status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setFreeCoinsClaimed(data.claimed);
        }
      } catch (error) {
        console.error('Error checking free coins status:', error);
      }
    };

    if (user && activeTab === 'free-coins') {
      checkFreeCoinStatus();
    }
  }, [user, activeTab]);

  // Fetch user cases data
  const fetchUserCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/user/cases`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCases(data.availableCases);
        setUserCases(data.userCases);
      }
    } catch (error) {
      console.error('Error fetching user cases:', error);
    }
  };

  // Fetch case history
  const fetchCaseHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/user/case-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCaseHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching case history:', error);
    }
  };

  // Fetch referral data
  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/referral/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      } else {
        console.error('Failed to fetch referral data:', response.status);
        // Set default data if API fails
        setReferralData({
          totalReferrals: user?.referralCount || 0,
          totalEarnings: user?.referralEarnings || 0,
          referrals: []
        });
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      // Set default data if API fails
      setReferralData({
        totalReferrals: user?.referralCount || 0,
        totalEarnings: user?.referralEarnings || 0,
        referrals: []
      });
    }
  };

  // Claim referral rewards
  const claimReferralRewards = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/referral/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update user balance
        const updatedUser = await fetch(`${BACKEND_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (updatedUser.ok) {
          const userData = await updatedUser.json();
          setUser(userData);
        }

        // Refresh referral data
        fetchReferralData();

        setTipStatus({ success: `Claimed $${(data.claimed || 0).toFixed(2)} in referral rewards!` });
        addNotification(`Claimed $${(data.claimed || 0).toFixed(2)} in referral rewards!`, 'success');
      } else {
        const error = await response.json();
        setTipStatus({ error: error.error || 'Failed to claim rewards' });
        addNotification(error.error || 'Failed to claim rewards', 'error');
      }
    } catch (error) {
      console.error('Error claiming rewards:', error);
      setTipStatus({ error: 'Failed to claim rewards' });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyReferralCode = async (e) => {
    e.preventDefault();
    if (!referralInputCode.trim()) return;

    setIsApplyingReferral(true);
    setApplyReferralStatus(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/referral/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ referralCode: referralInputCode.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        setApplyReferralStatus({ type: 'success', message: 'Referral code applied successfully!' });
        setReferralInputCode('');
        addNotification('Referral code applied successfully!', 'success');
        // Refresh referral data or user to show they are referred
        fetchReferralData();
      } else {
        setApplyReferralStatus({ type: 'error', message: data.error || 'Failed to apply code' });
        addNotification(data.error || 'Failed to apply code', 'error');
      }
    } catch (error) {
      console.error('Error applying referral code:', error);
      setApplyReferralStatus({ type: 'error', message: 'Server error. Please try again.' });
    } finally {
      setIsApplyingReferral(false);
    }
  };

  const handleChangeReferralCode = async () => {
    if (!newReferralCode.trim()) return;
    
    setIsChangingCode(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/referral/change-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newCode: newReferralCode.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        // Update user locally
        setUser(prev => ({ ...prev, referralCode: data.referralCode, referralCodeChanged: true }));
        setNewReferralCode('');
        addNotification('Referral code changed successfully!', 'success');
      } else {
        addNotification(data.error || 'Failed to change code', 'error');
      }
    } catch (error) {
      console.error('Error changing referral code:', error);
      addNotification('Server error. Please try again.', 'error');
    } finally {
      setIsChangingCode(false);
    }
  };

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'earn-with-us') {
      fetchReferralData();
    } else if (activeTab === 'rewards') {
      fetchUserCases();
      fetchCaseHistory();
    }
  }, [activeTab]);

  // Fetch wagering status
  const fetchWageringStatus = async () => {
    if (wageringLoading) return;

    try {
      setWageringLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/user/wagering-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(10000)
      });

      if (response.ok) {
        const data = await response.json();
        setWageringStatus(data);
        console.log('Wagering status loaded:', data);
      } else if (response.status === 401) {
        handleLogout();
      } else {
        console.error('Failed to fetch wagering status:', response.status);
        // Set default status if API fails
        setWageringStatus({
          canWithdraw: true,
          unwageredAmount: 0,
          totalWagered: user?.totalWagered || 0
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Wagering status fetch timed out');
      } else {
        console.error('Error fetching wagering status:', error);
      }
      // Set default status on error
      setWageringStatus({
        canWithdraw: true,
        unwageredAmount: 0,
        totalWagered: user?.totalWagered || 0
      });
    } finally {
      setWageringLoading(false);
    }
  };

  // Fetch user level data
  const [userLevel, setUserLevel] = useState(null);
  const [levelLoading, setLevelLoading] = useState(false);

  const fetchUserLevel = async () => {
    if (levelLoading) return;

    try {
      setLevelLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/api/user/level`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: AbortSignal.timeout(10000)
      });

      if (response.ok) {
        const data = await response.json();
        setUserLevel(data);
      } else if (response.status === 401) {
        handleLogout();
      } else {
        console.error('Failed to fetch user level:', response.status);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('User level fetch timed out');
      } else {
        console.error('Error fetching user level:', error);
      }
    } finally {
      setLevelLoading(false);
    }
  };

  // Fetch user data on component mount and when user changes
  useEffect(() => {
    if (user) {
      fetchUserLevel();
      fetchWageringStatus();
    }
  }, [user]);

  const handleLogout = () => {
    clickSound.current.play().catch(() => { });

    // FORCE CLEAR ALL SESSION DATA
    console.log('Logging out - clearing all session data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear any cached user data
    setUser(null);

    // Disconnect socket if it exists
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }

    // Force page reload to ensure clean state
    navigate('/dashboard');
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    setSending(true);
    sendMessage(chatInput.trim());
    setChatInput('');
    setSending(false);
  };

  const changeTab = (tab) => {
    if (tab === activeTab) return;
    
    if (LOCKED_TABS.includes(tab)) {
      if (window._addToast) {
        window._addToast({ 
          type: 'error', 
          title: 'LOCKED', 
          message: `${tab.charAt(0).toUpperCase() + tab.slice(1).replace('-', ' ')} is currently under maintenance.` 
        });
      }
      return;
    }

    clickSound.current.play().catch(() => { });

    // Update URL without page reload
    window.history.pushState({}, '', `/dashboard?tab=${tab}`);

    setActiveTab(tab);
    setTipStatus(null);
  };

  const handleTipSubmit = async (e) => {
    e.preventDefault();
    setTipStatus(null);
    if (tipUsername.trim().toLowerCase() === user.username.toLowerCase()) {
      setTipStatus({ error: "You cannot tip yourself." });
      return;
    }
    const amount = parseFloat(tipAmount);
    if (isNaN(amount) || amount <= 0) {
      setTipStatus({ error: "Please enter a valid tip amount greater than zero." });
      return;
    }
    const currentBalance = activeBalance === 'promo' ? (user.promoBalance || 0) : user.balance;
    if (amount > currentBalance) {
      setTipStatus({ error: `Insufficient ${activeBalance === 'promo' ? 'promo ' : ''}balance for this tip.` });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("User not authenticated");

      const response = await fetch(`${BACKEND_URL}/api/user/tip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipientUsername: tipUsername.trim(),
          amount,
          usePromoBalance: activeBalance === 'promo'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send tip');
      }

      if (activeBalance === 'promo') {
        setUser(prevUser => ({ ...prevUser, promoBalance: prevUser.promoBalance - amount }));
      } else {
        setUser(prevUser => ({ ...prevUser, balance: prevUser.balance - amount }));
      }

      setTipStatus({ success: `Successfully tipped $${amount.toFixed(2)} to ${tipUsername}.` });
      setTipUsername('');
      setTipAmount('');
    } finally {
      setLoading(false);
    }
  };

  const toggleChat = () => {
    clickSound.current.play().catch(() => { });
    const newState = !showChat;
    setShowChat(newState);
    localStorage.setItem('showChat', JSON.stringify(newState));
  };

  const toggleBalancePopup = (e) => {
    e.stopPropagation();
    clickSound.current.play().catch(() => { });
    setShowBalancePopup(!showBalancePopup);
  };

  const handleDeposit = () => {
    clickSound.current.play().catch(() => {});
    setShowBalancePopup(false);
    setIsDepositModalOpen(true);
  };

  const handleWithdraw = () => {
    if (!canWithdraw) {
      clickSound.current.play().catch(() => { });
      if(window._addToast) window._addToast({type:'error', title:'WITHDRAW ERROR', message:'Wagering requirements not met. Check your profile.'});
      return;
    }
    clickSound.current.play().catch(() => { });
    setShowBalancePopup(false);
    setIsWithdrawModalOpen(true);
  };

  const handleWallet = () => {
    clickSound.current.play().catch(() => { });
    setShowBalancePopup(false);
    changeTab('wallet');
  };

  // Handle balance updates from wallet component
  const handleBalanceUpdate = (newBalance) => {
    try {
      if (typeof newBalance === 'number' && newBalance >= 0) {
        setUser(prevUser => ({
          ...prevUser,
          balance: newBalance
        }));
      } else {
        console.warn('Invalid balance update received:', newBalance);
      }
    } catch (error) {
      console.error('Error updating balance:', error);
    }
  };

  // Updated free coins handler to show modal
  const handleFreeCoinsTask = async () => {
    if (freeCoinsClaimed) {
      if(window._addToast) window._addToast({type:'warning', title:'CLAIM ERROR', message:'You have already claimed your free scrap!'});
      return;
    }

    // Scroll to top to ensure the modal is visible
    window.scrollTo(0, 0);
    setShowFreeCoinsModal(true);
  };

  // Create a new function for the actual API call
  const claimFreeCoins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/user/claim-free-coins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prevUser => ({ ...prevUser, balance: data.newBalance }));
        setFreeCoinsClaimed(true);

        // Refresh cases data
        fetchUserCases();

        return {
          success: true,
          amount: data.amount,
          casesAwarded: data.casesAwarded
        };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to claim free coins');
      }
    } catch (error) {
      console.error('Error claiming free coins:', error);
      throw error;
    }
  };

  // Handle case opening with animation
  const handleOpenCase = async (caseType) => {
    if (openingCase) return;

    const caseCount = userCases[caseType] || 0;
    if (caseCount <= 0) {
      if(window._addToast) window._addToast({type:'error', title:'CASE ERROR', message:'You don\'t have any cases of this type!'});
      return;
    }

    // Show the case opening modal
    setSelectedCaseType(caseType);
    setShowCaseModal(true);
  };

  // API call for case opening (used by the animation component)
  const openCaseAPI = async (caseType) => {
    try {
      setOpeningCase(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/user/open-case/${caseType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Update user balance
        setUser(prevUser => ({ ...prevUser, balance: data.newBalance }));

        // Update case inventory
        setUserCases(prev => ({
          ...prev,
          [caseType]: data.remainingCases
        }));

        // Refresh case history
        fetchCaseHistory();

        return {
          success: true,
          item: data.item,
          newBalance: data.newBalance,
          remainingCases: data.remainingCases
        };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open case');
      }
    } catch (error) {
      console.error('Error opening case:', error);
      throw error;
    } finally {
      setOpeningCase(false);
    }
  };

  // Copy referral code
  const copyReferralCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      if(window._addToast) window._addToast({type:'success', title:'COPIED', message:'Referral code copied to clipboard!'});
    }).catch(() => {
      if(window._addToast) window._addToast({type:'error', title:'COPY ERROR', message:'Failed to copy referral code'});
    });
  };

  const GameCard = ({ title, gameKey, description, onClick, className, badge, image }) => (
    <motion.div
      whileHover={{ scale: 1.02, y: -8 }}
      whileTap={{ scale: 0.98 }}
      onMouseEnter={() => setHoveredGame(gameKey)}
      onMouseLeave={() => setHoveredGame(null)}
      onClick={(e) => {
        clickSound.current.play().catch(() => { });
        onClick?.(e);
      }}
      className={`game-card-premium ${className}`}
      role="button"
      tabIndex={0}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '0.85',
        borderRadius: '32px',
        overflow: 'hidden',
        cursor: 'pointer',
        background: '#0a0a0f',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '35px'
      }}
    >
      {/* Background Image */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.5s cubic-bezier(0.33, 1, 0.68, 1)',
          transform: hoveredGame === gameKey ? 'scale(1.1)' : 'scale(1)',
          opacity: 0.9
        }}
      />
      
      {/* Gradient Overlay for text readability */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 40%, transparent 100%)',
          zIndex: 1
        }}
      />

      <div style={{ position: 'relative', zIndex: 2 }}>
        <h2 style={{ 
          margin: 0, 
          color: 'white', 
          fontSize: '44px', 
          fontWeight: '950', 
          letterSpacing: '-1px',
          textTransform: 'uppercase',
          lineHeight: '1',
          marginBottom: '4px'
        }}>
          {title}
        </h2>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: '800', 
          color: 'rgba(255,255,255,0.5)', 
          textTransform: 'uppercase', 
          letterSpacing: '2px' 
        }}>
          RUSTYCOIN ORIGINALS
        </div>
      </div>

      {badge && (
        <div style={{ 
          position: 'absolute', 
          top: '25px', 
          right: '25px', 
          padding: '6px 14px', 
          background: 'linear-gradient(135deg, #ffb703, #ffb703)', 
          borderRadius: '12px', 
          color: 'white', 
          fontSize: '11px', 
          fontWeight: '900', 
          letterSpacing: '1px',
          zIndex: 3,
          boxShadow: '0 4px 15px rgba(124, 58, 237, 0.4)'
        }}>
          {badge}
        </div>
      )}
    </motion.div>
  );

  const renderTos = () => (
    <section className="terms-page-container modern">
      <div className="section-header-modern">
        <div className="header-title-group">
          <FaBook className="section-icon-modern" />
          <div className="title-text-group">
            <h2>Terms of Service</h2>
            <p className="subtitle-modern">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
      <div style={{ background: 'rgba(18, 18, 24, 0.6)', padding: '40px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', color: '#e2e8f0', marginTop: '20px' }}>
          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>1. Agreement to Terms</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              By accessing or using RustyCoin, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, do not use our services.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>2. Eligibility</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              You must be at least 18 years of age (or the legal age of majority in your jurisdiction) to use our platform. By using RustyCoin, you represent and warrant that you meet this requirement.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>3. User Accounts</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>4. Prohibited Activities</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              You may not use our platform for any illegal purpose. Prohibited activities include but are not limited to:
            </p>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>Using automated systems or bots to manipulate game outcomes.</li>
              <li>Attempting to circumvent any security features.</li>
              <li>Engaging in fraudulent activities or money laundering.</li>
              <li>Harassing other users or staff in the community chat.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>5. Responsible Gambling</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              Gambling should be for entertainment purposes only. Please play responsibly. If you feel you have a gambling problem, please seek professional help.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>6. Limitation of Liability</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              RustyCoin is provided "as is" without warranty of any kind. We shall not be liable for any indirect, incidental, special, or consequential damages.
            </p>
          </section>
      </div>
    </section>
  );

  const renderPrivacy = () => (
    <section className="privacy-page-container modern">
      <div className="section-header-modern">
        <div className="header-title-group">
          <FaBook className="section-icon-modern" />
          <div className="title-text-group">
            <h2>Privacy Policy</h2>
            <p className="subtitle-modern">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
      <div style={{ background: 'rgba(18, 18, 24, 0.6)', padding: '40px', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', color: '#e2e8f0', marginTop: '20px' }}>
          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>1. Information We Collect</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We collect information that you provide to us directly when you create an account, such as your username, email address, and IP address.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>2. How We Use Informatiom</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We use your information to:
            </p>
            <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>Provide and maintain our services.</li>
              <li>Communicate with you regarding your account or our services.</li>
              <li>Monitor and analyze trends, usage, and activities.</li>
              <li>Detect and prevent fraudulent or unauthorized activities.</li>
            </ul>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>3. Data Security</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We take reasonable measures to protect your information from loss, theft, misuse, unauthorized access, disclosure, alteration, and destruction.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>4. Cookies and Tracking</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We use cookies and similar technologies to track your activity on our platform and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>5. Third-Party Services</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We may use third-party services, such as Google Analytics, to analyze our traffic. These third parties have their own privacy policies.
            </p>
          </section>

          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>6. Changes to This Policy</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
            </p>
          </section>
          
          <section style={{ marginBottom: '30px' }}>
            <h2 style={{ color: '#ffb703', fontSize: '20px', marginBottom: '15px' }}>7. Contact Us</h2>
            <p style={{ lineHeight: '1.6', marginBottom: '15px' }}>
              If you have any questions about this Privacy Policy, please contact us via live support.
            </p>
          </section>
      </div>
    </section>
  );

  const renderHome = () => (
    <>
      {/* Hero Banners */}
      <div className="home-banners-row">
        <div 
          className="home-banner home-banner--left" 
          onClick={() => {
            if (!user) {
              setAuthModalView('login');
              setIsAuthModalOpen(true);
            } else {
              navigate('/case-battles');
            }
          }} 
          role="button" 
          tabIndex={0}
        >
          <img src="/casebattle.jpg" alt="Case Battle Banner" className="home-banner-img" />
          <div className="home-banner-overlay">
            <span className="home-banner-label">{user ? 'Battle Now →' : 'Log In to Battle →'}</span>
          </div>
        </div>

        <div 
          className="home-banner home-banner--right" 
          onClick={() => {
            if (!user) {
              setAuthModalView('login');
              setIsAuthModalOpen(true);
            } else {
              if (window._addToast) {
                window._addToast({ type: 'error', title: 'LOCKED', message: 'Rewards are currently under maintenance.' });
              }
            }
          }} 
          role="button" 
          tabIndex={0}
        >
          <img src="/secondbanner.png" alt="Rewards Banner" className="home-banner-img" />
          <div className="home-banner-overlay">
            <span className="home-banner-label">Explore Rewards →</span>
          </div>
        </div>
      </div>

      {/* Games Section */}
      <div className="home-games-section">
        <div className="home-section-header">
          <div className="home-section-label">
            <span className="home-section-line" />
            <span className="home-section-text">Games</span>
            <span className="home-section-line" />
          </div>
        </div>
        <div className="games-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          <GameCard
            title="COINFLIP"
            gameKey="coinflip"
            image="/cfpng.png"
            onClick={() => navigate('/coinflip')}
            badge="HOT"
          />

          <GameCard
            title="JACKPOT"
            gameKey="jackpot"
            image="/jppng.png"
            onClick={() => navigate('/jackpot')}
            badge="BIG POT"
          />

          <GameCard
            title="CASES"
            gameKey="cases"
            image="/cases.png"
            onClick={() => navigate('/cases')}
            badge="HOT"
          />

          <GameCard
            title="BATTLES"
            gameKey="battles"
            image="/casebattle.jpg"
            onClick={() => navigate('/case-battles')}
            badge="NEW"
          />

          <GameCard
            title="BUST"
            gameKey="bust"
            image="/bust.jpg"
            onClick={() => navigate('/bust')}
            badge="NEW"
          />
        </div>
      </div>
    </>
  );



  // Listen for level up events
  useEffect(() => {
    if (socket) {
      const handleLevelUp = (data) => {
        try {
          if (!data || !data.newLevel || !data.levelName) {
            console.error('Invalid level up data received:', data);
            return;
          }

          // UI notifications for level up have been removed as per user request.
          // Still refresh stats to keep UI consistent.
          fetchUserLevel();
          fetchUserCases(); // Refresh cases after level up

        } catch (error) {
          console.error('Error handling level up event:', error);
        }
      };

      socket.on('level_up', handleLevelUp);

      return () => {
        if (socket) {
          socket.off('level_up', handleLevelUp);
        }
      };
    }
  }, [socket, clickVolume]);



  const renderLeaderboard = () => (
    <section className="leaderboard-container modern">
      <div className="coming-soon-modern">
        <div className="section-header-modern">
          <div className="header-title-group">
            <FaTrophy className="section-icon-modern" />
            <div className="title-text-group">
              <h2>Global Leaderboard</h2>
              <p className="subtitle-modern">Compete for the top spots and win massive prizes</p>
            </div>
          </div>
        </div>
        <div className="coming-soon-content-modern" style={{ padding: '60px 20px', textAlign: 'center', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '20px', border: '1px border rgba(255,255,255,0.05)', marginTop: '20px' }}>
          <div className="coming-soon-icon-container" style={{ fontSize: '80px', color: 'rgba(74, 108, 255, 0.2)', marginBottom: '20px' }}>
            <FaChartLine />
          </div>
          <div className="soon-badge">Coming Soon</div>
          <h3 style={{ fontSize: '24px', margin: '15px 0' }}>The Competition is Heating Up</h3>
          <p style={{ maxWidth: '500px', margin: '0 auto 20px', color: '#8c94ca' }}>We are currently aggregating data to launch the most competitive leaderboard in crypto gaming. Stay tuned for rankings, prizes, and glory.</p>
          <div className="waitlist-indicator">
            <span className="dot pulse" style={{ background: '#4ade80' }}></span> Stand by for deployment
          </div>
        </div>
      </div>
    </section>
  );



  const renderFreeCoins = () => {
    const handleClaimDaily = () => {
        addNotification('Daily reward claimed! +$0.05 Scrap', 'success');
        // Logic for backend call would go here
    };

    return (
      <section className="rewards-redesign" style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
        <div className="section-header-modern" style={{ marginBottom: '40px' }}>
          <div className="header-title-group">
            <FaCoins className="section-icon-modern" style={{ color: '#ffb703' }} />
            <div className="title-text-group">
              <h2>Free Rewards</h2>
              <p className="subtitle-modern">Locked for maintenance</p>
            </div>
          </div>
        </div>

        <div className="coming-soon-content-modern" style={{ padding: '60px 20px', textAlign: 'center', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', marginTop: '20px' }}>
          <div className="coming-soon-icon-container" style={{ fontSize: '80px', color: 'rgba(251, 191, 36, 0.2)', marginBottom: '20px' }}>
            <FaLock />
          </div>
          <div className="soon-badge" style={{ background: '#ef4444' }}>Under Maintenance</div>
          <h3 style={{ fontSize: '24px', margin: '15px 0' }}>This feature is temporarily unavailable</h3>
          <p style={{ maxWidth: '500px', margin: '0 auto 20px', color: '#8c94ca' }}>We are currently performing maintenance on the rewards system to bring you a better experience. Please check back later.</p>
        </div>
      </section>
    );
  };

  const renderProfile = () => {
    const userLvl = userLevel?.userLevel;
    const currentLevelInfo = userLevel?.levels?.[(userLvl?.current || 1) - 1];
    const BRAND_PURPLE = '#ffb703';
    
    const sidebarTabs = [
      { id: 'profile', label: 'Profile', icon: FaUser },
      { id: 'inventory', label: 'Inventory', icon: FaBoxes },
      { id: 'security', label: 'Security', icon: FaShieldAlt },
      { id: 'history', label: 'Game History', icon: FaHistory },
      { id: 'transactions', label: 'Transactions', icon: FaExchangeAlt },
      { id: 'logout', label: 'Log Out', icon: FaSignOutAlt, isAction: true }
    ];

    const renderProfileContent = () => {
      switch(profileTab) {
        case 'profile':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Level Progress */}
              <div style={{ background: '#1c1c24', padding: '25px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '900', color: 'white' }}>LEVEL {userLvl?.current || 1}</span>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{Math.floor(userLvl?.xp || 0)} / 100 XP</span>
                </div>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ width: `${userLvl?.progress || 0}%`, height: '100%', background: BRAND_PURPLE, borderRadius: '10px', boxShadow: `0 0 10px ${BRAND_PURPLE}44` }}></div>
                </div>
              </div>

              {/* Username */}
              <div style={{ background: '#1c1c24', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <FaUser style={{ color: BRAND_PURPLE }} />
                  <span style={{ fontWeight: '800', color: 'white', fontSize: '14px' }}>Username</span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    value={usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    placeholder="Enter new username..." 
                    style={{ flex: 1, background: '#13131a', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', color: 'white', fontSize: '12px' }}
                  />
                  <button 
                    onClick={handleUpdateUsername}
                    disabled={isUpdatingUsername}
                    style={{ background: BRAND_PURPLE, border: 'none', color: 'white', padding: '0 20px', borderRadius: '10px', fontWeight: '800', fontSize: '12px', cursor: 'pointer', opacity: isUpdatingUsername ? 0.5 : 1 }}>
                    {isUpdatingUsername ? 'Saving...' : 'Save'}
                  </button>
                </div>
                {usernameStatus && <div style={{ fontSize: '10px', marginTop: '8px', color: usernameStatus.type === 'success' ? '#4ade80' : '#ef4444', fontWeight: '800' }}>{usernameStatus.text}</div>}
              </div>

              {/* Trade Link */}
              <div style={{ background: '#1c1c24', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <FaLink style={{ color: BRAND_PURPLE }} />
                  <span style={{ fontWeight: '800', color: 'white', fontSize: '14px' }}>Trade Link</span>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    value={tradeLinkInput}
                    onChange={(e) => setTradeLinkInput(e.target.value)}
                    placeholder="https://steamcommunity.com/tradeoffer/new/..." 
                    style={{ flex: 1, background: '#13131a', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', color: 'white', fontSize: '12px' }}
                  />
                  <button style={{ background: BRAND_PURPLE, border: 'none', color: 'white', padding: '0 20px', borderRadius: '10px', fontWeight: '800', fontSize: '12px', cursor: 'pointer' }}>Save</button>
                </div>
              </div>

              {/* Email */}
              <div style={{ background: '#1c1c24', padding: '20px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <FaEnvelope style={{ color: BRAND_PURPLE }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: '800', color: 'white', fontSize: '14px' }}>Email</span>
                    {user?.email && <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', fontWeight: '700' }}>Current: {user.email}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="email" 
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter new email..." 
                    style={{ flex: 1, background: '#13131a', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', color: 'white', fontSize: '12px' }}
                  />
                  <button 
                    onClick={handleUpdateEmail}
                    disabled={isUpdatingEmail}
                    style={{ background: BRAND_PURPLE, border: 'none', color: 'white', padding: '0 20px', borderRadius: '10px', fontWeight: '800', fontSize: '12px', cursor: 'pointer', opacity: isUpdatingEmail ? 0.5 : 1 }}>
                    {isUpdatingEmail ? 'Saving...' : 'Save'}
                  </button>
                </div>
                {emailStatus && <div style={{ fontSize: '10px', marginTop: '8px', color: emailStatus.type === 'success' ? '#4ade80' : '#ef4444', fontWeight: '800' }}>{emailStatus.text}</div>}
              </div>

              {/* User Stats */}
              <div style={{ background: '#1c1c24', padding: '25px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                  <FaChartLine style={{ color: BRAND_PURPLE }} />
                  <span style={{ fontWeight: '800', color: 'white', fontSize: '14px' }}>User Stats</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  {[
                    { label: 'Wagered', val: `$${(user?.totalWagered || 0).toFixed(2)}`, icon: FaChartBar },
                    { label: 'Jackpot', val: `$${(userLevel?.gameStats?.jackpot?.wagered || 0).toFixed(2)}`, icon: FaTrophy },
                    { label: 'Coinflip', val: `$${(userLevel?.gameStats?.coinflip?.wagered || 0).toFixed(2)}`, icon: FaSyncAlt },
                    { label: 'Cases', val: `$${(userLevel?.gameStats?.cases?.wagered || 0).toFixed(2)}`, icon: FaBoxes }
                  ].map((stat, i) => (
                    <div key={i} style={{ background: '#13131a', padding: '15px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <stat.icon style={{ color: BRAND_PURPLE, fontSize: '12px' }} />
                        <span style={{ fontSize: '10px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>{stat.label}</span>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '900', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}>
                         <ScrapIcon size={12} /> {stat.val}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        case 'inventory':
          return (
            <div style={{ background: '#1c1c24', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', minHeight: '400px' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '900', color: 'white' }}>INVENTORY</h3>
              <div className="cases-grid">
                {availableCases.map((caseData) => {
                  const userCaseCount = userCases[caseData.id] || 0;
                  return (
                    <div key={caseData.id} className="case-item" style={{ background: '#13131a' }}>
                      <div className="case-icon"><FaBoxes /></div>
                      <div className="case-details">
                        <h4>{caseData.name}</h4>
                        <p>Count: {userCaseCount}</p>
                      </div>
                      <button className="case-open-btn" onClick={() => handleOpenCase(caseData.id)} disabled={userCaseCount <= 0 || openingCase}>
                        {openingCase ? <FaSpinner className="fa-spin" /> : 'Open'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        case 'history':
          return (
            <div style={{ background: '#1c1c24', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', minHeight: '400px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                 <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: 'white' }}>GAME HISTORY</h3>
                 <div style={{ padding: '6px 12px', background: 'rgba(74, 222, 128, 0.1)', color: '#4ade80', borderRadius: '8px', fontSize: '11px', fontWeight: '800' }}>
                   PROVABLY FAIR ACTIVE
                 </div>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'left' }}>
                      <th style={{ padding: '15px' }}>GAME</th>
                      <th style={{ padding: '15px' }}>DATE</th>
                      <th style={{ padding: '15px' }}>WAGER</th>
                      <th style={{ padding: '15px' }}>PROFIT</th>
                      <th style={{ padding: '15px' }}>STATUS</th>
                      <th style={{ padding: '15px' }}>VERIFY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gameWagers.length === 0 ? (
                      <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.2)' }}>No history found</td></tr>
                    ) : (
                      gameWagers.slice(0, historyLimit).map((w, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', color: 'white' }}>
                          <td style={{ padding: '15px', fontWeight: '800', textTransform: 'uppercase' }}>{w.gameType}</td>
                          <td style={{ padding: '15px', color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{new Date(w.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                             <ScrapIcon size={12} /> ${w.amount.toFixed(2)}
                          </td>
                          <td style={{ padding: '15px', color: w.profit > 0 ? '#4ade80' : w.profit < 0 ? '#ef4444' : 'white' }}>
                            {w.profit > 0 ? '+' : ''}{w.profit.toFixed(2)}
                          </td>
                          <td style={{ padding: '15px' }}>
                            <span style={{ 
                                padding: '4px 8px', 
                                borderRadius: '6px', 
                                fontSize: '10px', 
                                fontWeight: '900',
                                background: w.outcome === 'win' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: w.outcome === 'win' ? '#4ade80' : '#ef4444'
                            }}>
                                {w.outcome.toUpperCase()}
                            </span>
                          </td>
                          <td style={{ padding: '15px' }}>
                             <button 
                               onClick={() => { setVerifyingWager(w); setShowVerifyModal(true); }}
                               style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: BRAND_PURPLE, padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '800' }}>
                               VERIFY
                             </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {gameWagers.length > historyLimit && (
                 <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button 
                      onClick={() => setHistoryLimit(prev => prev + 5)}
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '12px', fontWeight: '800', fontSize: '12px', cursor: 'pointer' }}>
                      LOAD MORE
                    </button>
                 </div>
              )}
            </div>
          );
        case 'security':
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ background: '#1c1c24', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                   <FaShieldAlt style={{ color: BRAND_PURPLE }} />
                   <span style={{ fontWeight: '800', color: 'white', fontSize: '16px' }}>Provably Fair Settings</span>
                 </div>
                 <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '20px', lineHeight: '1.6' }}>
                    Reveal and rotate your active server seed to verify past game outcomes. Once revealed, the seed can never be used again.
                 </p>
                 <div style={{ background: '#13131a', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.02)', marginBottom: '15px' }}>
                    <div style={{ fontSize: '10px', color: BRAND_PURPLE, fontWeight: '900', marginBottom: '5px' }}>ACTIVE SERVER SEED (HASHED)</div>
                    <div style={{ fontSize: '12px', color: 'white', fontWeight: '800', wordBreak: 'break-all' }}>6e7c2a2fea90eb626d60ce1b6... (Hashed for Security)</div>
                 </div>
                 <button 
                  onClick={async () => {
                    try {
                      const res = await userAPI.rotateSeed();
                      if (res.data.success) {
                        addNotification(`Success! Old seed revealed: ${res.data.revealedOldSeed.substring(0, 10)}...`, 'success');
                      }
                    } catch (err) {
                      addNotification('Failed to rotate seed', 'error');
                    }
                  }}
                  style={{ width: '100%', background: BRAND_PURPLE, color: 'white', border: 'none', padding: '14px', borderRadius: '10px', fontWeight: '900', fontSize: '13px', cursor: 'pointer' }}>
                    ROTATE ACTIVE SEED
                 </button>
              </div>

              <div style={{ background: '#1c1c24', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                   <FaFingerprint style={{ color: BRAND_PURPLE }} />
                   <span style={{ fontWeight: '800', color: 'white', fontSize: '16px' }}>Session History</span>
                 </div>
                 <div style={{ color: 'rgba(255,255,255,0.2)', textAlign: 'center', padding: '20px', fontSize: '12px' }}>No other active sessions.</div>
              </div>
            </div>
          );
        case 'transactions':
          return (
            <div style={{ background: '#1c1c24', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', minHeight: '400px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                 <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: 'white' }}>TRANSACTION HISTORY</h3>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'left' }}>
                      <th style={{ padding: '15px' }}>TYPE</th>
                      <th style={{ padding: '15px' }}>DATE</th>
                      <th style={{ padding: '15px' }}>AMOUNT</th>
                      <th style={{ padding: '15px' }}>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.2)' }}>No transactions found</td></tr>
                    ) : (
                      transactions.map((t, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', color: 'white' }}>
                          <td style={{ padding: '15px' }}>
                            <span style={{ 
                                padding: '4px 8px', 
                                borderRadius: '6px', 
                                fontSize: '10px', 
                                fontWeight: '900',
                                background: t.type === 'deposit' ? 'rgba(34, 197, 94, 0.1)' : t.type === 'withdrawal' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(124, 58, 237, 0.1)',
                                color: t.type === 'deposit' ? '#22c55e' : t.type === 'withdrawal' ? '#ef4444' : '#ffb703',
                                textTransform: 'uppercase'
                            }}>
                                {t.type}
                            </span>
                          </td>
                          <td style={{ padding: '15px', color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>{new Date(t.createdAt).toLocaleDateString()}</td>
                          <td style={{ padding: '15px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                             <ScrapIcon size={12} /> {t.amount.toFixed(2)}
                          </td>
                          <td style={{ padding: '15px' }}>
                            <span style={{ 
                                color: t.status === 'completed' ? '#4ade80' : t.status === 'pending' ? '#fbbf24' : '#ef4444',
                                fontWeight: '800',
                                fontSize: '11px',
                                textTransform: 'uppercase'
                            }}>
                                {t.status || 'COMPLETED'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
      }
    };

    return (
      <section className="profile-redesign" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 3fr', gap: '30px', padding: '20px' }}>
        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* User Preview Card */}
          <div style={{ background: '#16161c', borderRadius: '24px', padding: '25px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.05)' }}>
             <div style={{ width: '80px', height: '80px', margin: '0 auto 15px', borderRadius: '20px', overflow: 'hidden', border: `2px solid ${BRAND_PURPLE}` }}>
                <img src={user?.avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=fallback'} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
             </div>
             <h3 style={{ fontSize: '18px', fontWeight: '900', color: 'white', margin: '0 0 5px 0' }}>{user?.username}</h3>
             <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontWeight: '800' }}>ID: {user?._id?.substring(0, 8)}</div>
          </div>

          <div style={{ background: '#16161c', borderRadius: '20px', padding: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ padding: '15px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '11px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Wager Requirement</span>
              <span style={{ color: BRAND_PURPLE, fontWeight: '900', fontSize: '13px' }}>
                ${(wageringStatus?.progress?.remaining || 0).toFixed(2)}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {sidebarTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => tab.isAction ? handleLogout() : setProfileTab(tab.id)}
                  style={{
                    background: profileTab === tab.id ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
                    border: '1px solid',
                    borderColor: profileTab === tab.id ? 'rgba(124, 58, 237, 0.3)' : 'transparent',
                    padding: '14px 20px',
                    borderRadius: '16px',
                    color: profileTab === tab.id ? '#fff' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontSize: '14px',
                    fontWeight: '700',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    if (profileTab !== tab.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (profileTab !== tab.id) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#94a3b8';
                    }
                  }}
                >
                  <tab.icon size={16} style={{ color: profileTab === tab.id ? '#ffb703' : 'inherit' }} /> 
                  <span style={{ flex: 1 }}>{tab.label}</span>
                  {profileTab === tab.id && <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#ffb703', boxShadow: '0 0 10px #ffb703' }} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div style={{ animation: 'fadeIn 0.3s ease' }}>
          {renderProfileContent()}
        </div>
      </section>
    );
  };

  const renderWallet = () => (
    <section className="wallet-section">
      <Wallet
        apiUrl={BACKEND_URL}
        onBalanceUpdate={handleBalanceUpdate}
        socketInstance={socket}
      />
    </section>
  );

  const renderEarnWithUs = () => (
    <section className="affiliates-redesign" style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px 20px 60px' }}>
      <div style={{ marginBottom: '48px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ffb703', fontSize: '12px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px' }}>
            <FaUsers /> Affiliate Program
          </div>
          <h2 style={{ fontSize: '42px', fontWeight: 950, color: '#fff', margin: 0, lineHeight: 1 }}>AFFILIATES</h2>
          <p style={{ color: '#94a3b8', fontSize: '16px', fontWeight: 500, marginTop: '12px', maxWidth: '400px' }}>
            Earn passive income by inviting your community to play on RustyCoin.
          </p>
        </div>
        
        {referralData?.pendingRewards > 0 && (
          <button 
            onClick={claimReferralRewards}
            disabled={loading}
            style={{ 
              background: 'linear-gradient(135deg, #ffb703 0%, #ffb703 100%)',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '16px',
              fontWeight: '900',
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 15px 30px -10px rgba(124, 58, 237, 0.5)',
              marginBottom: '4px'
            }}
          >
            {loading ? <FaSpinner className="fa-spin" /> : <FaMoneyBillWave />}
            CLAIM ${(referralData.pendingRewards || 0).toFixed(2)}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '40px' }}>
        {[
          { label: 'Referrals', val: user?.referralCount || 0, icon: FaUsers, color: '#ffb703' },
          { label: 'Total Earnings', val: `$${(user?.referralEarnings || 0).toFixed(2)}`, icon: FaCoins, color: '#ffb703' },
          { label: 'Active Today', val: '0', icon: FaChartLine, color: '#4ade80' },
          { label: 'Tier Status', val: 'BASIC', icon: FaTrophy, color: '#ffb703' }
        ].map((stat, i) => (
          <div key={i} style={{ background: '#1a1b23', padding: '25px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <stat.icon style={{ color: stat.color, fontSize: '20px' }} />
            <div style={{ fontSize: '11px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>{stat.label}</div>
            <div style={{ fontSize: '20px', fontWeight: '900', color: 'white' }}>{stat.val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '30px' }}>
        <div style={{ background: '#13141a', borderRadius: '24px', padding: '35px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '900', color: 'white', marginBottom: '25px' }}>YOUR REFERRAL CODE</h3>
          <div style={{ background: '#1a1b23', border: '2px dashed rgba(255,255,255,0.1)', padding: '25px', borderRadius: '18px', textAlign: 'center', position: 'relative' }}>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontWeight: '800', marginBottom: '10px' }}>CLICK TO COPY LINK</div>
            <div 
              onClick={() => {
                const link = `${window.location.origin}/dashboard?tab=free-coins&ref=${user?.referralCode}`;
                navigator.clipboard.writeText(link);
                addNotification('Referral link copied!', 'success');
              }}
              style={{ fontSize: '22px', fontWeight: '900', color: '#ffb703', letterSpacing: '4px', cursor: 'pointer', transition: '0.2s' }}
              onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}
            >
              {user?.referralCode?.toUpperCase() || 'LOGIN TO SEE'}
            </div>
            
            {!user?.referralCodeChanged && user && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    placeholder="CUSTOM CODE..."
                    value={newReferralCode}
                    onChange={(e) => setNewReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    style={{ flex: 1, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '10px 15px', color: 'white', fontWeight: '800', fontSize: '12px' }}
                  />
                  <button 
                    onClick={handleChangeReferralCode}
                    disabled={isChangingCode || newReferralCode.length < 3}
                    style={{ background: '#ffb703', color: 'white', border: 'none', padding: '0 15px', borderRadius: '10px', fontWeight: '900', fontSize: '11px', cursor: 'pointer', opacity: (isChangingCode || newReferralCode.length < 3) ? 0.5 : 1 }}
                  >
                    {isChangingCode ? <FaSpinner className="fa-spin" /> : 'SET CODE'}
                  </button>
                </div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.2)', marginTop: '8px', fontWeight: '700' }}>
                  ONE-TIME CHANGE ONLY
                </div>
              </div>
            )}
          </div>
          <div style={{ marginTop: '30px', fontSize: '13px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.6' }}>
            Invite your friends using your unique referral link. You will receive 1% of their total wagers directly to your affiliate balance. 
            There is no limit to how much you can earn!
          </div>
        </div>

        <div style={{ background: '#13141a', borderRadius: '24px', padding: '35px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '900', color: 'white', marginBottom: '25px' }}>HOW IT WORKS</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {[
              { num: '1', title: 'Share Link', desc: 'Send your referral link to your friends or share it on social media.' },
              { num: '2', title: 'They Play', desc: 'When your friends join and start wagering, you start earning.' },
              { num: '3', title: 'Collect Scrap', desc: 'Your earnings are added to your account instantly.' }
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '20px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: 'rgba(124, 58, 237, 0.1)', color: '#ffb703', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', flexShrink: 0 }}>{step.num}</div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '900', color: 'white', marginBottom: '5px' }}>{step.title}</div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.4' }}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '30px', marginTop: '30px' }}>
        {/* Apply Referral Code Section */}
        <div style={{ background: '#13141a', borderRadius: '24px', padding: '35px', border: '1px solid rgba(255,255,255,0.05)' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '900', color: 'white', marginBottom: '15px' }}>APPLY REFERRAL CODE</h3>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '25px', lineHeight: '1.5' }}>
            Joining someone's network grants you special bonuses. If you're already referred, you can change your code once every 3 days.
          </p>
          
          <form onSubmit={handleApplyReferralCode} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{ position: 'relative' }}>
              <FaPlus style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.2)' }} />
              <input 
                type="text" 
                placeholder="ENTER CODE..." 
                value={referralInputCode}
                onChange={(e) => setReferralInputCode(e.target.value.toUpperCase())}
                style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px', padding: '16px 16px 16px 45px', color: 'white', fontWeight: '800', letterSpacing: '1px' }}
              />
            </div>
            <button 
              type="submit"
              disabled={isApplyingReferral || !referralInputCode.trim()}
              style={{ background: '#ffb703', color: 'white', border: 'none', padding: '16px', borderRadius: '12px', fontWeight: '900', fontSize: '14px', cursor: 'pointer', opacity: isApplyingReferral ? 0.5 : 1 }}
            >
              {isApplyingReferral ? <FaSpinner className="fa-spin" /> : 'APPLY CODE'}
            </button>
          </form>
          
          {applyReferralStatus && (
            <div style={{ marginTop: '15px', padding: '12px', borderRadius: '8px', background: applyReferralStatus.type === 'success' ? 'rgba(74, 222, 128, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: applyReferralStatus.type === 'success' ? '#4ade80' : '#ef4444', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {applyReferralStatus.type === 'success' ? <FaCheck /> : <FaExclamationCircle />}
              {applyReferralStatus.message}
            </div>
          )}
        </div>

        {/* Current Referrer Info (if any) */}
        <div style={{ background: '#13141a', borderRadius: '24px', padding: '35px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {user?.referredBy ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4ade80', margin: '0 auto 20px' }}>
                <FaCheck size={28} />
              </div>
              <h4 style={{ fontSize: '16px', fontWeight: '900', color: 'white', marginBottom: '10px' }}>YOU ARE REFERRED</h4>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: '1.5' }}>
                You are currently part of a referral network. Your wagering helps support your referrer!
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '60px', height: '60px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.1)', margin: '0 auto 20px' }}>
                <FaUsers size={28} />
              </div>
              <h4 style={{ fontSize: '16px', fontWeight: '900', color: 'white', marginBottom: '10px' }}>NOT REFERRED</h4>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: '1.5' }}>
                You haven't joined any network yet. Use a friend's code to get started!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Referred Users Table */}
      <div style={{ marginTop: '40px', background: '#13141a', borderRadius: '24px', padding: '35px', border: '1px solid rgba(255,255,255,0.05)' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '900', color: 'white', marginBottom: '25px' }}>YOUR REFERRED USERS</h3>
        
        {(!referralData?.referredUsers || referralData.referredUsers.length === 0) ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.02)', borderRadius: '16px' }}>
            <FaUsers size={40} style={{ marginBottom: '15px', opacity: 0.5 }} />
            <p>You haven't referred anyone yet. Share your link to start earning!</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'left' }}>
                  <th style={{ padding: '15px' }}>USERNAME</th>
                  <th style={{ padding: '15px' }}>DEPOSITED</th>
                  <th style={{ padding: '15px' }}>WAGERED</th>
                  <th style={{ padding: '15px' }}>COMMISSION</th>
                  <th style={{ padding: '15px' }}>STATUS</th>
                  <th style={{ padding: '15px' }}>JOINED</th>
                </tr>
              </thead>
              <tbody>
                {referralData.referredUsers.slice(0, referralLimit).map((ref, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', color: 'white' }}>
                    <td style={{ padding: '15px', fontWeight: '800' }}>{ref.username}</td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ScrapIcon size={12} /> ${(ref.totalDeposited || 0).toFixed(2)}
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ScrapIcon size={12} /> ${(ref.totalWagered || 0).toFixed(2)}
                      </div>
                    </td>
                    <td style={{ padding: '15px', color: '#4ade80', fontWeight: '800' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ScrapIcon size={12} /> +${Number(ref.commission || 0).toFixed(2)}
                      </div>
                    </td>
                    <td style={{ padding: '15px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '6px', background: 'rgba(74, 222, 128, 0.1)', color: '#4ade80', fontSize: '10px', fontWeight: '900' }}>
                        ACTIVE
                      </span>
                    </td>
                    <td style={{ padding: '15px', color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                      {new Date(ref.joinedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {referralData.referredUsers.length > referralLimit && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button 
                  onClick={() => setReferralLimit(prev => prev + 5)}
                  style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', padding: '12px 30px', borderRadius: '12px', fontWeight: '900', fontSize: '12px', cursor: 'pointer', transition: '0.2s' }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.05)'}
                >
                  LOAD MORE
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );

  const renderRewards = () => (
    <section className="rewards-redesign" style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
       <div className="section-header-modern" style={{ marginBottom: '40px' }}>
        <div className="header-title-group">
          <FaGift className="section-icon-modern" style={{ color: '#ef4444' }} />
          <div className="title-text-group">
            <h2>Rewards</h2>
            <p className="subtitle-modern">Locked for maintenance</p>
          </div>
        </div>
      </div>

      <div className="coming-soon-content-modern" style={{ padding: '60px 20px', textAlign: 'center', background: 'rgba(30, 41, 59, 0.5)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.05)', marginTop: '20px' }}>
        <div className="coming-soon-icon-container" style={{ fontSize: '80px', color: 'rgba(239, 68, 68, 0.2)', marginBottom: '20px' }}>
          <FaLock />
        </div>
        <div className="soon-badge" style={{ background: '#ef4444' }}>Under Maintenance</div>
        <h3 style={{ fontSize: '24px', margin: '15px 0' }}>The rewards vault is closed</h3>
        <p style={{ maxWidth: '500px', margin: '0 auto 20px', color: '#8c94ca' }}>We are currently upgrading the rewards system. All earned rewards are safe and will be available once maintenance is complete.</p>
      </div>
    </section>
  );



  const renderLearnMore = () => (
    <section className="learn-more-container modern">
      <div className="section-header-modern">
        <div className="header-title-group">
          <FaBook className="section-icon-modern" />
          <div className="title-text-group">
            <h2>Learn More</h2>
            <p className="subtitle-modern">Everything you need to know about RustyCoin</p>
          </div>
        </div>
      </div>

      <div className="learn-content">
        <div className="learn-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaGamepad style={{ color: 'var(--accent-light)' }} /> What is RustyCoin?</h3>
          <p>RustyCoin is a modern gambling platform that offers exciting games with fair odds and instant payouts. Our platform combines the thrill of traditional casino games with innovative mechanics and cryptocurrency integration.</p>
          <p>We prioritize user experience, security, and responsible gaming to provide the best possible entertainment for our players.</p>
        </div>

        <div className="learn-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaBullseye style={{ color: 'var(--accent-light)' }} /> How to Get Started</h3>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Create Account</h4>
              <p>Sign up with your email and create a secure password. Verification is quick and easy.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Make Deposit</h4>
              <p>Add funds to your account using various payment methods including crypto and traditional payments.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Start Playing</h4>
              <p>Choose your favorite game and start winning! All games have different strategies and odds.</p>
            </div>
          </div>
        </div>

        <div className="learn-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaShieldAlt style={{ color: '#4ade80' }} /> Security & Fairness</h3>
          <div className="security-features">
            <div className="security-item">
              <FaCheck className="security-icon" />
              <div>
                <h4>Provably Fair</h4>
                <p>All games use cryptographic algorithms that can be verified for fairness.</p>
              </div>
            </div>
            <div className="security-item">
              <FaCheck className="security-icon" />
              <div>
                <h4>Secure Transactions</h4>
                <p>Your deposits and withdrawals are protected with industry-standard encryption.</p>
              </div>
            </div>
            <div className="security-item">
              <FaCheck className="security-icon" />
              <div>
                <h4>Responsible Gaming</h4>
                <p>We provide tools and resources to help you gamble responsibly.</p>
              </div>
            </div>
            <div className="security-item">
              <FaCheck className="security-icon" />
              <div>
                <h4>24/7 Support</h4>
                <p>Our support team is available around the clock to assist you.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="learn-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaCoins style={{ color: '#ffb703' }} /> Rewards & Bonuses</h3>
          <div className="rewards-info">
            <div className="reward-item">
              <FaGift className="reward-icon" />
              <div>
                <h4>Welcome Bonus</h4>
                <p>New players receive free coins to get started with our games.</p>
              </div>
            </div>
            <div className="reward-item">
              <FaLevelUpAlt className="reward-icon" />
              <div>
                <h4>Level System</h4>
                <p>Earn XP by playing games and unlock rewards as you level up.</p>
              </div>
            </div>
            <div className="reward-item">
              <FaUsers className="reward-icon" />
              <div>
                <h4>Referral Program</h4>
                <p>Invite friends and earn 1% of their wagering as commission.</p>
              </div>
            </div>
            <div className="reward-item">
              <FaTrophy className="reward-icon" />
              <div>
                <h4>VIP Program</h4>
                <p>High-volume players get exclusive bonuses and priority support.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="learn-section">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><FaExclamationTriangle style={{ color: '#f87171' }} /> Responsible Gaming</h3>
          <div className="responsible-gaming">
            <p>We are committed to promoting responsible gambling. Please remember:</p>
            <ul>
              <li>Set deposit and loss limits before you start playing</li>
              <li>Never gamble with money you can't afford to lose</li>
              <li>Take regular breaks and don't chase losses</li>
              <li>Gambling should be fun, not a way to make money</li>
              <li>If you feel you have a gambling problem, seek help immediately</li>
            </ul>
            <div className="help-resources">
              <h4>Need Help?</h4>
              <p>If you or someone you know needs help with gambling addiction:</p>
              <div className="help-links">
                <a href="https://www.gamblingtherapy.org" target="_blank" rel="noopener noreferrer">Gambling Therapy</a>
                <a href="https://www.gamblersanonymous.org" target="_blank" rel="noopener noreferrer">Gamblers Anonymous</a>
                <a href="https://www.ncpgambling.org" target="_blank" rel="noopener noreferrer">National Council on Problem Gambling</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderSettings = () => (
    <section className="settings-container modern">
      <div className="section-header-modern">
        <div className="header-title-group">
          <FaCog className="section-icon-modern" />
          <div className="title-text-group">
            <h2>Settings</h2>
            <p className="subtitle-modern">Customize your experience and security</p>
          </div>
        </div>
      </div>
      <div className="settings-section">
        <h3>Muted Users</h3>
        {mutedUsers.length === 0 ? (
          <p style={{ color: '#8c94ca', background: 'rgba(255,255,255,0.02)', padding: '20px', borderRadius: '10px', textAlign: 'center' }}>
            <FaMinusCircle style={{ display: 'block', margin: '0 auto 10px', fontSize: '24px', opacity: 0.3 }} />
            No muted users.
          </p>
        ) : (
          <ul className="muted-users-list">
            {mutedUsers.map((username) => (
              <li key={username}>
                {username} <button onClick={() => setMutedUsers(mutedUsers.filter(u => u !== username))}>Unmute</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="settings-section">
        <h3>🔊 Click Sound Volume</h3>
        <div className="volume-control">
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={clickVolume}
            onChange={(e) => {
              const volume = parseFloat(e.target.value);
              setClickVolume(volume);
              localStorage.setItem('clickVolume', volume);
            }}
            style={{
              background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${clickVolume * 100}%, rgba(255, 255, 255, 0.1) ${clickVolume * 100}%, rgba(255, 255, 255, 0.1) 100%)`
            }}
          />
          <span>{(clickVolume * 100).toFixed(0)}%</span>
        </div>
      </div>
      <div className="settings-section">
        <h3>💡 Screen Brightness</h3>
        <div className="volume-control">
          <input
            type="range"
            min="0.5"
            max="1.5"
            step="0.05"
            value={brightness}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              setBrightness(value);
              localStorage.setItem('brightness', value);
            }}
            style={{
              background: `linear-gradient(to right, var(--accent) 0%, var(--accent) ${((brightness - 0.5) / 1) * 100}%, rgba(255, 255, 255, 0.1) ${((brightness - 0.5) / 1) * 100}%, rgba(255, 255, 255, 0.1) 100%)`
            }}
          />
          <span>{(brightness * 100).toFixed(0)}%</span>
        </div>
      </div>
    </section>
  );

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  return (
    <div className="dashboard-main-only">
      {/* Dashboard Sub-Navigation */}
      <div className="rc-dashboardNav" style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 90, 
        background: 'rgba(13, 17, 23, 0.9)', 
        backdropFilter: 'blur(25px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '32px'
      }}>
        {[
          { id: 'rewards', label: 'Rewards', icon: FaGift },
          { id: 'earn-with-us', label: 'Affiliates', icon: FaUsers },
          { id: 'leaderboard', label: 'Leaderboard', icon: FaTrophy },
          { id: 'free-coins', label: 'Free Coins', icon: FaCoins }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => changeTab(t.id)}
            className={`rc-dashTab ${activeTab === t.id ? 'active' : ''}`}
            style={{
              background: activeTab === t.id ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
              border: '1px solid',
              borderColor: activeTab === t.id ? 'rgba(124, 58, 237, 0.3)' : 'transparent',
              padding: '10px 24px',
              borderRadius: '14px',
              color: activeTab === t.id ? '#fff' : '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              fontSize: '13px',
              fontWeight: 800,
              position: 'relative'
            }}
          >
            <t.icon style={{ color: activeTab === t.id ? '#ffb703' : 'inherit', fontSize: '16px' }} />
            <span>{t.label}</span>
            {activeTab === t.id && (
              <motion.div 
                layoutId="activeTabIndicator"
                style={{ position: 'absolute', bottom: '-4px', left: '25%', right: '25%', height: '2px', background: '#ffb703', borderRadius: '2px', boxShadow: '0 0 10px #ffb703' }} 
              />
            )}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="main-content-standalone" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)', marginBottom: '0' }}>
        <main>
          <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                layout
              >
                {activeTab === 'home' && renderHome()}
                {activeTab === 'earn-with-us' && renderEarnWithUs()}
                {activeTab === 'leaderboard' && renderLeaderboard()}
                {activeTab === 'rewards' && renderRewards()}
                {activeTab === 'free-coins' && renderFreeCoins()}
                {activeTab === 'learn-more' && renderLearnMore()}
                {activeTab === 'profile' && renderProfile()}
                {activeTab === 'wallet' && renderWallet()}
                {activeTab === 'settings' && renderSettings()}
                {activeTab === 'tos' && renderTos()}
                {activeTab === 'privacy' && renderPrivacy()}
              </motion.div>
          </AnimatePresence>
        </main>

        <Footer />
      </div>


      {/* Toast Notifications */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 100000, display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'none' }}>
        <AnimatePresence>
          {notifications.map(n => (
            <motion.div
              key={n.id}
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
              style={{
                pointerEvents: 'auto',
                background: n.type === 'error' ? 'rgba(231, 76, 60, 0.95)' : 'rgba(52, 152, 219, 0.95)',
                color: 'white',
                padding: '12px 20px',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '280px',
                maxWidth: '400px',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              <div style={{ fontSize: '20px' }}>
                {n.type === 'error' ? '🚫' : '🔔'}
              </div>
              <div style={{ flex: 1 }}>{n.message}</div>
              <button 
                onClick={() => setNotifications(prev => prev.filter(notif => notif.id !== n.id))}
                style={{ background: 'none', border: 'none', color: 'white', opacity: 0.7, cursor: 'pointer', fontSize: '18px' }}
              >
                ×
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Case Opening Modal */}
      <CaseOpeningAnimation
        isOpen={showCaseModal}
        onClose={() => setShowCaseModal(false)}
        caseType={selectedCaseType}
        onOpenCase={openCaseAPI}
      />

      {renderVerifyModal()}
    </div>
  );
}

export default Dashboard;

